import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useSelector } from 'react-redux';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
    faBolt, faGift, faStar, faFire, faTag, faTags, faShoppingCart, faShoppingBag,
    faHeart, faTrophy, faTicketAlt, faLink, faVideo, faGamepad, faGlobe, faStore,
    faPercent, faCoins, faBell, faGem, faWandMagicSparkles,
} from '@fortawesome/free-solid-svg-icons';
import { faFacebook, faTiktok } from '@fortawesome/free-brands-svg-icons';
import utilityBlockApi, { UTILITY_ORIGIN } from '../../api/utilityBlockApi';
import { matchesAudience, viewerFromAuth } from '../../utils/audience-visibility';

// Map tên FontAwesome (admin nhập) → icon. PHẢI khớp app + admin (utility-icon-map.js)
// để preview admin = icon web render. Không có trong map thì fallback faLink.
const ICON_MAP = {
    faBolt, faGift, faStar, faFire, faTag, faTags, faShoppingCart, faShoppingBag,
    faHeart, faTrophy, faTicketAlt, faLink, faVideo, faGamepad, faGlobe, faStore,
    faPercent, faCoins, faBell, faGem, faWandMagicSparkles, faFacebook, faTiktok,
};

// openMode='screen' (màn native app) → route tương ứng trên web. Block trỏ tới
// screen không có route web sẽ bị ẩn (tránh tile chết). Khớp SCREEN_WHITELIST app.
const SCREEN_ROUTES = {
    Vouchers: '/voucher',
    Referral: '/user-info/ref',
    Cashback: '/shopee',
    MyOrders: '/user-info/order',
    PriceWatch: '/price-watch',
    LinkConverter: '/link-converter',
    ShopeeVideo: '/shopeevideo',
    Withdraw: '/user-info/withdraw',
    Leaderboard: '/top_orders',
    SavedProducts: '/saved-products',
    Notifications: '/notification',
    Settings: '/user-info',
    Help: '/help',
    CommunityFeed: '/cong-dong',
};

// Gắn userId vào link nếu bật injectUserId (đồng bộ build-utility-url.ts của app).
function buildUtilityUrl(link, injectUserId, userId) {
    if (!injectUserId || userId == null || userId === '') return link;
    const uid = encodeURIComponent(String(userId));
    if (link.includes('{userId}')) return link.replace(/\{userId\}/g, uid);
    return link + (link.includes('?') ? '&' : '?') + 'user_id=' + uid;
}

function Utilitys() {
    const navigate = useNavigate();
    const { isLogined, userInfo } = useSelector((state) => state.auth);
    const userId = userInfo?.userId || userInfo?.id;
    const [blocks, setBlocks] = useState([]);

    useEffect(() => {
        let alive = true;
        utilityBlockApi
            .fetchUtilityConfig()
            .then((cfg) => alive && setBlocks(cfg.blocks))
            .catch(() => {});
        return () => {
            alive = false;
        };
    }, []);

    // Lọc theo lịch hiện (file tĩnh không đổi realtime) + ẩn 'screen' không có route web
    // + lọc theo đối tượng (file tĩnh dùng chung nên backend không lọc hộ được).
    const now = Date.now() / 1000;
    const viewer = viewerFromAuth(isLogined, userInfo);
    const visible = blocks.filter((b) => {
        if (b.startAt && b.startAt > now) return false;
        if (b.endAt && b.endAt < now) return false;
        if (!matchesAudience(b, viewer)) return false;
        if (b.openMode === 'screen') return !!SCREEN_ROUTES[b.screenName];
        return true;
    });

    // Ẩn hẳn section khi không có block nào (không chiếm chỗ).
    if (!visible.length) return null;

    const handlePress = (block) => {
        utilityBlockApi.trackUtilityClick(block.id); // đếm lượt bấm (fire-and-forget)

        // Block yêu cầu đăng nhập mà chưa login → đẩy sang Login, không mở link.
        if (block.requireLogin && !isLogined) {
            navigate('/login');
            return;
        }

        // Mode 'screen' → điều hướng route web nội bộ (chỉ trong SCREEN_ROUTES).
        if (block.openMode === 'screen') {
            const path = SCREEN_ROUTES[block.screenName];
            if (path) navigate(path);
            return;
        }

        const url = buildUtilityUrl(block.link, block.injectUserId, userId);
        if (!/^https?:\/\//i.test(url)) return; // chỉ http/https (chặn tel:/javascript:)
        // Web không có in-app webview → mở tab mới cho cả appview/webview/external.
        window.open(url, '_blank', 'noopener,noreferrer');
    };

    return (
        <div className="bg-surface p-2">
            <p className="font-semibold">Khám phá thêm</p>
            <div className="grid grid-cols-4 gap-2 mt-2 select-none">
                {visible.map((block) => (
                    <button
                        key={block.id}
                        type="button"
                        onClick={() => handlePress(block)}
                        className="flex flex-col items-center gap-1 p-1 rounded-lg hover:bg-surface-sunken"
                    >
                        <div className="relative">
                            <div
                                className="w-12 h-12 rounded-xl flex items-center justify-center overflow-hidden"
                                style={{ backgroundColor: block.iconBg || '#FFF1F0' }}
                            >
                                {block.imagePath ? (
                                    <img
                                        src={`${UTILITY_ORIGIN}${block.imagePath}`}
                                        alt={block.title}
                                        className="w-full h-full object-cover"
                                    />
                                ) : (
                                    <FontAwesomeIcon
                                        icon={ICON_MAP[block.iconName] || faLink}
                                        style={{ color: block.iconColor || '#ff544f' }}
                                        className="text-lg"
                                    />
                                )}
                            </div>
                            {block.badge ? (
                                <span className="absolute -top-1 -right-2 bg-brand-red text-white text-[9px] leading-none font-bold px-1 py-0.5 rounded-full">
                                    {block.badge}
                                </span>
                            ) : null}
                        </div>
                        <span className="text-center text-xs text-content-2 leading-tight line-clamp-2">
                            {block.title}
                        </span>
                    </button>
                ))}
            </div>
        </div>
    );
}

export default Utilitys;
